import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ticket, TicketStatus } from '../../entities/ticket';
import { getTickets } from '../../entities/ticket/api/ticketApi';
import * as S from './SearchPage.styles';

export const SearchPage: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    clientName: '',
    assignedTo: '',
    status: '',
    priority: '',
    site: '',
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadTickets();
  }, [filters]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const allTickets = await getTickets({
        search: filters.search || undefined,
        site: filters.site || undefined,
        priority: filters.priority || undefined,
        status: filters.status ? [filters.status] : undefined,
      });
      
      let filtered = allTickets;

      if (filters.clientName) {
        const clientLower = filters.clientName.toLowerCase();
        filtered = filtered.filter(t => 
          t.client.name.toLowerCase().includes(clientLower) ||
          t.client.mainContact?.email?.toLowerCase().includes(clientLower)
        );
      }

      if (filters.assignedTo) {
        const assignedLower = filters.assignedTo.toLowerCase();
        filtered = filtered.filter(t => 
          t.assignedTo?.toLowerCase().includes(assignedLower)
        );
      }

      setTickets(filtered);
    } catch (error) {
      console.error('Ошибка поиска тикетов:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTicketClick = (ticketId: string) => {
    navigate(`/tickets?ticket=${ticketId}`);
  };

  const getStatusLabel = (status: TicketStatus): string => {
    const labels: Record<TicketStatus, string> = {
      new: 'Новый',
      active: 'Активен',
      waiting_info: 'Ожидаем ответ клиента',
      in_programming: 'В программирование',
      resolved: 'Решено',
      closed: 'Закрыто',
      spam: 'Спам',
      duplicate: 'Дубликат',
    };
    return labels[status] || status;
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      clientName: '',
      assignedTo: '',
      status: '',
      priority: '',
      site: '',
    });
  };

  return (
    <S.SearchContainer>
      <S.SearchHeader>
        <S.SearchTitle>Поиск тикетов</S.SearchTitle>
      </S.SearchHeader>

      <S.FiltersSection>
        <S.FiltersGrid>
          <S.FilterGroup>
            <S.FilterLabel>Поиск по названию</S.FilterLabel>
            <S.FilterInput
              type="text"
              placeholder="Введите название тикета..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </S.FilterGroup>

          <S.FilterGroup>
            <S.FilterLabel>Клиент</S.FilterLabel>
            <S.FilterInput
              type="text"
              placeholder="Имя клиента или email..."
              value={filters.clientName}
              onChange={(e) => handleFilterChange('clientName', e.target.value)}
            />
          </S.FilterGroup>

          <S.FilterGroup>
            <S.FilterLabel>Исполнитель</S.FilterLabel>
            <S.FilterInput
              type="text"
              placeholder="Имя исполнителя..."
              value={filters.assignedTo}
              onChange={(e) => handleFilterChange('assignedTo', e.target.value)}
            />
          </S.FilterGroup>

          <S.FilterGroup>
            <S.FilterLabel>Статус</S.FilterLabel>
            <S.FilterSelect
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">Все</option>
              <option value="new">Новый</option>
              <option value="active">Открыто</option>
              <option value="waiting_info">Ожидаем ответ клиента</option>
              <option value="in_programming">В программирование</option>
              <option value="resolved">Решено</option>
              <option value="closed">Закрыто</option>
              <option value="spam">Спам</option>
              <option value="duplicate">Дубликат</option>
            </S.FilterSelect>
          </S.FilterGroup>

          <S.FilterGroup>
            <S.FilterLabel>Приоритет</S.FilterLabel>
            <S.FilterSelect
              value={filters.priority}
              onChange={(e) => handleFilterChange('priority', e.target.value)}
            >
              <option value="">Все</option>
              <option value="low">Низкий</option>
              <option value="medium">Средний</option>
              <option value="high">Высокий</option>
              <option value="urgent">Срочный</option>
            </S.FilterSelect>
          </S.FilterGroup>

          <S.FilterGroup>
            <S.FilterLabel>Сайт</S.FilterLabel>
            <S.FilterSelect
              value={filters.site}
              onChange={(e) => handleFilterChange('site', e.target.value)}
            >
              <option value="">Все</option>
              <option value="hudle">Hudle</option>
              <option value="tournament">Турнирный сайт</option>
            </S.FilterSelect>
          </S.FilterGroup>
        </S.FiltersGrid>

        <S.FilterActions>
          <S.ClearButton onClick={clearFilters}>
            Очистить фильтры
          </S.ClearButton>
          <S.ResultsCount>
            Найдено: {tickets.length}
          </S.ResultsCount>
        </S.FilterActions>
      </S.FiltersSection>

      <S.ResultsSection>
        {loading ? (
          <S.Loading>Загрузка...</S.Loading>
        ) : tickets.length === 0 ? (
          <S.EmptyState>
            <S.EmptyStateText>Тикеты не найдены</S.EmptyStateText>
            <S.EmptyStateSubtext>
              Попробуйте изменить параметры поиска
            </S.EmptyStateSubtext>
          </S.EmptyState>
        ) : (
          <S.TicketsList>
            {tickets.map(ticket => (
              <S.TicketCard
                key={ticket.id}
                onClick={() => handleTicketClick(ticket.id)}
              >
                <S.TicketCardHeader>
                  <S.TicketCardTitle>{ticket.title}</S.TicketCardTitle>
                  <S.TicketCardStatus $status={ticket.status}>
                    {getStatusLabel(ticket.status)}
                  </S.TicketCardStatus>
                </S.TicketCardHeader>
                <S.TicketCardInfo>
                  <S.TicketCardClient>Клиент: {ticket.client.name}</S.TicketCardClient>
                  <S.TicketCardSite $site={ticket.site}>
                    {ticket.site === 'hudle' ? 'Hudle' : ticket.site === 'unknown' ? 'Неизвестный' : 'Турнир'}
                  </S.TicketCardSite>
                  <S.TicketCardPriority $priority={ticket.priority}>
                    Приоритет: {ticket.priority}
                  </S.TicketCardPriority>
                </S.TicketCardInfo>
                {ticket.lastMessage && (
                  <S.TicketCardPreview>
                    {ticket.lastMessage.content.substring(0, 100)}...
                  </S.TicketCardPreview>
                )}
              </S.TicketCard>
            ))}
          </S.TicketsList>
        )}
      </S.ResultsSection>
    </S.SearchContainer>
  );
};
